/**
 * Feature flags: every flag the product knows about, and the one function
 * that decides what a flag is worth for a given caller.
 *
 * A flag is *declared* here and *managed* in the admin. The declaration is
 * the part that cannot be wrong — its type and its safe default — and the
 * database only ever holds state layered on top: a master switch, ordered
 * targeting rules and a fallthrough value. So code can never ask for a
 * flag nobody defined, a row can never change what type a flag is, and a
 * missing row, a row that no longer validates or an API that cannot be
 * reached all land on the same answer: the default written below.
 *
 * `evaluateFlag` is pure and shared, so the API, the admin's preview panel
 * and the tests cannot disagree about what a rule means.
 */
/** Everywhere a flag can be read. `api` is the server itself. */
export const FLAG_SURFACES = ['api', 'web', 'partner', 'admin', 'partner-app', 'client-app'];
/** What the caller is running on: a browser, or one of the two phone OSes. */
export const FLAG_PLATFORMS = ['web', 'ios', 'android'];
export const FLAG_TYPES = ['boolean', 'string', 'number', 'json'];
export const FLAGS = {
    'app.maintenanceBanner': {
        key: 'app.maintenanceBanner',
        type: 'json',
        default: null,
        label: 'Maintenance banner',
        description: 'A banner shown across the top of every app, for planned downtime or an incident. null shows nothing. Shape: { "message": string, "tone": "info" | "warning" | "critical", "href"?: string }.',
        surfaces: ['web', 'partner', 'admin', 'partner-app', 'client-app'],
        validate: (value) => {
            if (value === null)
                return null;
            if (typeof value !== 'object' || Array.isArray(value))
                return 'must be an object or null';
            if (typeof value.message !== 'string' || !value.message.trim())
                return 'message is required';
            if (!['info', 'warning', 'critical'].includes(value.tone)) {
                return 'tone must be info, warning or critical';
            }
            if (value.href !== undefined && typeof value.href !== 'string')
                return 'href must be a string';
            return null;
        },
    },
    'messaging.editMessages': {
        key: 'messaging.editMessages',
        type: 'boolean',
        default: false,
        label: 'Edit and delete messages',
        description: 'Let people edit or delete a message they have sent in a support or hire thread. Off hides the actions and the API refuses them.',
        surfaces: ['api', 'web', 'partner', 'admin', 'partner-app', 'client-app'],
    },
    'fleet.scanRegistration': {
        key: 'fleet.scanRegistration',
        type: 'boolean',
        default: false,
        label: 'Scan a registration document',
        description: 'Offer "Scan the registration document" on Add a car in the partner app, which reads the photo with a vision model and fills in the form. Needs VISION_ENABLED on the API. Off hides the button and the API refuses the scan.',
        surfaces: ['api', 'partner-app'],
    },
    'content.aiWriting': {
        key: 'content.aiWriting',
        type: 'boolean',
        default: false,
        label: 'AI writing help in the content editor',
        description: 'Offer "Write with AI" in the dashboard\'s page editor: draft, rewrite, shorten, simplify, fix or translate text. Suggestions are reviewed before they enter a draft. Needs WRITER_ENABLED on the API. Off hides the button and the API refuses the request.',
        surfaces: ['api', 'admin'],
    },
};
export const FLAG_KEYS = Object.keys(FLAGS);
export function isFlagKey(key) {
    return Object.prototype.hasOwnProperty.call(FLAGS, key);
}
/** Every flag a surface reads, which is what the apps are sent. */
export function flagKeysFor(surface) {
    return FLAG_KEYS.filter((key) => FLAGS[key].surfaces.includes(surface));
}
// ── State ────────────────────────────────────────────────────────────────
/** Rollout is in basis points so 0.01% steps are possible: 10000 is everyone. */
export const FLAG_ROLLOUT_ALL = 10_000;
export const FLAG_MAX_RULES = 50;
export const FLAG_MAX_IDS_PER_LIST = 1_000;
/** The largest a JSON value may be, serialised, so every app's payload stays small. */
export const FLAG_MAX_VALUE_BYTES = 16 * 1024;
/** An empty, disabled state — what a flag with no row behaves like. */
export function emptyFlagState(key) {
    return { enabled: false, rules: [], fallthrough: FLAGS[key].default };
}
// ── Evaluation ───────────────────────────────────────────────────────────
/**
 * FNV-1a, 32-bit. Not cryptographic, and does not need to be: it only has
 * to spread ids evenly and give the same answer in Node, a browser and
 * Hermes, which a hand-written loop over char codes does.
 */
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
/**
 * The unit a rollout is decided for. A partner's whole team shares one
 * answer — half a rental office seeing a new screen is a support call —
 * and a signed-in customer beats the device they happen to be on.
 */
export function flagUnit(ctx) {
    return ctx.partnerId ?? ctx.customerId ?? ctx.anonymousId;
}
/**
 * Where a unit falls for a flag, 0–9999. Salted with the key so being in
 * the first 10% of one rollout says nothing about the next, and sticky, so
 * widening a rollout from 10% to 20% only ever adds people.
 */
export function flagBucket(key, unit) {
    return fnv1a(`${key}:${unit}`) % FLAG_ROLLOUT_ALL;
}
/**
 * Compares two dotted versions numerically, ignoring any `-beta` or `+build`
 * suffix. Null when either side is not a version.
 */
export function compareVersions(a, b) {
    const parse = (v) => {
        const core = v.trim().replace(/^v/i, '').split(/[-+]/)[0] ?? '';
        if (!/^\d+(\.\d+){0,3}$/.test(core))
            return null;
        return core.split('.').map(Number);
    };
    const left = parse(a);
    const right = parse(b);
    if (!left || !right)
        return null;
    for (let i = 0; i < Math.max(left.length, right.length); i++) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0)
            return Math.sign(diff);
    }
    return 0;
}
export function ruleMatches(key, rule, ctx) {
    if (rule.surfaces.length && (!ctx.surface || !rule.surfaces.includes(ctx.surface)))
        return false;
    if (rule.platforms.length && (!ctx.platform || !rule.platforms.includes(ctx.platform)))
        return false;
    if (rule.minAppVersion) {
        const cmp = ctx.appVersion ? compareVersions(ctx.appVersion, rule.minAppVersion) : null;
        if (cmp === null || cmp < 0)
            return false;
    }
    const named = rule.customerIds.length + rule.partnerIds.length + rule.partnerUserIds.length > 0;
    if (named) {
        const listed = (!!ctx.customerId && rule.customerIds.includes(ctx.customerId)) ||
            (!!ctx.partnerId && rule.partnerIds.includes(ctx.partnerId)) ||
            (!!ctx.partnerUserId && rule.partnerUserIds.includes(ctx.partnerUserId));
        if (!listed)
            return false;
    }
    if (rule.rolloutBasisPoints < FLAG_ROLLOUT_ALL) {
        if (rule.rolloutBasisPoints <= 0)
            return false;
        const unit = flagUnit(ctx);
        // Nobody to be sticky about: a partial rollout cannot place them.
        if (!unit)
            return false;
        if (flagBucket(key, unit) >= rule.rolloutBasisPoints)
            return false;
    }
    return true;
}
/**
 * What a flag is worth for `ctx`, and why.
 *
 * `state` is expected to have passed `validateFlagState`; the API drops a
 * row that does not, so an unvalidated state never reaches here in
 * practice.
 */
export function evaluateFlag(key, state, ctx = {}) {
    const fallback = FLAGS[key].default;
    if (!state)
        return { value: fallback, reason: 'default' };
    if (!state.enabled)
        return { value: fallback, reason: 'off' };
    for (const rule of state.rules) {
        if (ruleMatches(key, rule, ctx))
            return { value: rule.value, reason: `rule:${rule.id}` };
    }
    return { value: state.fallthrough, reason: 'fallthrough' };
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RULE_ID = /^[A-Za-z0-9_-]{1,40}$/;
/** Checks one value against a flag's declared type, options and shape. */
export function validateFlagValue(key, value) {
    const def = FLAGS[key];
    switch (def.type) {
        case 'boolean':
            if (typeof value !== 'boolean')
                return { ok: false, error: 'must be true or false' };
            return { ok: true, value };
        case 'number':
            if (typeof value !== 'number' || !Number.isFinite(value))
                return { ok: false, error: 'must be a number' };
            return { ok: true, value };
        case 'string':
            if (typeof value !== 'string')
                return { ok: false, error: 'must be text' };
            if (def.options && !def.options.includes(value)) {
                return { ok: false, error: `must be one of ${def.options.join(', ')}` };
            }
            return { ok: true, value };
        case 'json': {
            let text;
            try {
                text = JSON.stringify(value);
            }
            catch {
                return { ok: false, error: 'must be valid JSON' };
            }
            if (text === undefined)
                return { ok: false, error: 'must be valid JSON' };
            if (text.length > FLAG_MAX_VALUE_BYTES)
                return { ok: false, error: 'is too large' };
            // Round-trip, so what is stored is exactly what an app will be sent.
            const parsed = JSON.parse(text);
            const problem = def.validate?.(parsed) ?? null;
            if (problem)
                return { ok: false, error: problem };
            return { ok: true, value: parsed };
        }
    }
}
function stringList(raw, field, check) {
    if (raw === undefined || raw === null)
        return { ok: true, value: [] };
    if (!Array.isArray(raw))
        return { ok: false, error: `${field} must be a list` };
    if (raw.length > FLAG_MAX_IDS_PER_LIST) {
        return { ok: false, error: `${field} can hold at most ${FLAG_MAX_IDS_PER_LIST} entries` };
    }
    const out = [];
    for (const item of raw) {
        const text = typeof item === 'string' ? item.trim() : '';
        if (!check(text))
            return { ok: false, error: `${field} has an invalid entry "${String(item)}"` };
        if (!out.includes(text))
            out.push(text);
    }
    return { ok: true, value: out };
}
const isSurface = (s) => FLAG_SURFACES.includes(s);
const isPlatform = (s) => FLAG_PLATFORMS.includes(s);
const isUuid = (s) => UUID.test(s);
function validateRule(key, raw, index) {
    const at = `Rule ${index + 1}`;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { ok: false, error: `${at} is not an object` };
    const input = raw;
    if (typeof input.id !== 'string' || !RULE_ID.test(input.id))
        return { ok: false, error: `${at} has an invalid id` };
    const surfaces = stringList(input.surfaces, `${at}: surfaces`, isSurface);
    if (!surfaces.ok)
        return surfaces;
    const declared = FLAGS[key].surfaces;
    const stray = surfaces.value.find((s) => !declared.includes(s));
    if (stray)
        return { ok: false, error: `${at}: "${stray}" never reads this flag` };
    const platforms = stringList(input.platforms, `${at}: platforms`, isPlatform);
    if (!platforms.ok)
        return platforms;
    let minAppVersion = null;
    if (input.minAppVersion !== undefined && input.minAppVersion !== null && input.minAppVersion !== '') {
        if (typeof input.minAppVersion !== 'string' || compareVersions(input.minAppVersion, '0') === null) {
            return { ok: false, error: `${at}: minimum version must look like 1.4.0` };
        }
        minAppVersion = input.minAppVersion.trim();
    }
    const customerIds = stringList(input.customerIds, `${at}: customers`, isUuid);
    if (!customerIds.ok)
        return customerIds;
    const partnerIds = stringList(input.partnerIds, `${at}: partners`, isUuid);
    if (!partnerIds.ok)
        return partnerIds;
    const partnerUserIds = stringList(input.partnerUserIds, `${at}: partner users`, isUuid);
    if (!partnerUserIds.ok)
        return partnerUserIds;
    const bps = input.rolloutBasisPoints ?? FLAG_ROLLOUT_ALL;
    if (typeof bps !== 'number' || !Number.isInteger(bps) || bps < 0 || bps > FLAG_ROLLOUT_ALL) {
        return { ok: false, error: `${at}: rollout must be between 0% and 100%` };
    }
    const value = validateFlagValue(key, input.value);
    if (!value.ok)
        return { ok: false, error: `${at}: value ${value.error}` };
    if (input.note !== undefined && input.note !== null && typeof input.note !== 'string') {
        return { ok: false, error: `${at}: note must be text` };
    }
    const note = typeof input.note === 'string' && input.note.trim() ? input.note.trim().slice(0, 500) : null;
    return {
        ok: true,
        value: {
            id: input.id,
            surfaces: surfaces.value,
            platforms: platforms.value,
            minAppVersion,
            customerIds: customerIds.value,
            partnerIds: partnerIds.value,
            partnerUserIds: partnerUserIds.value,
            rolloutBasisPoints: bps,
            value: value.value,
            note,
        },
    };
}
/**
 * Checks a whole state against the flag's declaration and returns it
 * normalised — trimmed, de-duplicated, defaults filled in — which is the
 * form that is stored.
 */
export function validateFlagState(key, raw) {
    if (!isFlagKey(key))
        return { ok: false, error: `Unknown flag "${key}"` };
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { ok: false, error: 'State must be an object' };
    const input = raw;
    if (typeof input.enabled !== 'boolean')
        return { ok: false, error: 'enabled must be true or false' };
    const fallthrough = validateFlagValue(key, input.fallthrough);
    if (!fallthrough.ok)
        return { ok: false, error: `Fallthrough value ${fallthrough.error}` };
    const rawRules = input.rules ?? [];
    if (!Array.isArray(rawRules))
        return { ok: false, error: 'rules must be a list' };
    if (rawRules.length > FLAG_MAX_RULES)
        return { ok: false, error: `At most ${FLAG_MAX_RULES} rules` };
    const rules = [];
    for (const [index, rawRule] of rawRules.entries()) {
        const rule = validateRule(key, rawRule, index);
        if (!rule.ok)
            return rule;
        if (rules.some((r) => r.id === rule.value.id))
            return { ok: false, error: `Rule ${index + 1} repeats an id` };
        rules.push(rule.value);
    }
    return { ok: true, value: { enabled: input.enabled, rules, fallthrough: fallthrough.value } };
}
